import { BgColorOptions, ColorOptions, RatioOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { CUSTOMER_GROUPS, customerGroupVisibilityCmsField } from '../../../../../customer/groups/customer-groups';
import { IPageComponentSubmenuBlockData } from './submenu.interface';

export const config = <Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'>>{
    id: 'submenu',
    name: 'Submenu',
    description: 'Meganav submenu component',
    tags: ['submenu', 'meganav'],
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'columns',
            title: 'Columns',
            itemName: 'Column',
            maxSize: 5,
            defaultSize: 1,
            complex: true,
            fields: [
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'blocks',
                    title: 'Blocks',
                    itemName: 'Block',
                    defaultSize: 1,
                    complex: true,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            key: 'type',
                            title: 'Type',
                            options: [
                                { key: 'links', title: 'Links' },
                                { key: 'images', title: 'Images' },
                                { key: 'lists', title: 'Lists' },
                                { key: 'colour_block', title: 'Colour block' }
                            ],
                            default: 'links'
                        },
                        /**
                         * Links
                         */
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'links',
                            title: 'Links',
                            itemName: 'Link',
                            defaultSize: 1,
                            complex: true,
                            fields: [
                                {
                                    type: NICmsContentField.EFieldType.label,
                                    key: 'section-links-link',
                                    title: 'Link'
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'text',
                                    title: 'Text',
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'link',
                                    title: 'Link',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'text_style',
                                    title: 'Text Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'text_color',
                                    title: 'Text Color',
                                    options: ColorOptions,
                                    colorPreview: true,
                                    default: ''
                                },
                                {
                                    type: NICmsContentField.EFieldType.label,
                                    key: 'section-links-label',
                                    title: 'Label'
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'label_text',
                                    title: 'Text',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'label_text_color',
                                    title: 'Text Color',
                                    options: ColorOptions,
                                    colorPreview: true,
                                    default: ''
                                },
                                {
                                    type: NICmsContentField.EFieldType.label,
                                    key: 'section-links-image',
                                    title: 'Image'
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.image,
                                    key: 'image',
                                    title: 'Image',
                                    default: ''
                                },
                                customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
                            ],
                            canShow: (data: IPageComponentSubmenuBlockData) => data?.type === 'links'
                        },
                        /**
                         * Images
                         */
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'images',
                            title: 'Images',
                            itemName: 'Image',
                            defaultSize: 1,
                            maxSize: 2,
                            complex: true,
                            fields: [
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.image,
                                    key: 'image',
                                    title: 'Image',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'ratio',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Ratio',
                                    options: RatioOptions,
                                    default: '3-4'
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'text',
                                    title: 'Text',
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'link',
                                    title: 'Link',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'text_style',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Text Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'text_color',
                                    title: 'Text Color',
                                    options: ColorOptions,
                                    colorPreview: true,
                                    default: ''
                                },
                                customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
                            ],
                            canShow: (data: IPageComponentSubmenuBlockData) => data?.type === 'images'
                        },
                        /**
                         * Lists
                         */
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'lists',
                            title: 'Lists',
                            itemName: 'List Item',
                            defaultSize: 1,
                            complex: true,
                            fields: [
                                {
                                    type: NICmsContentField.EFieldType.label,
                                    key: 'section-list',
                                    title: 'List'
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'text',
                                    title: 'Text',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'text_color',
                                    title: 'Text Color',
                                    options: ColorOptions,
                                    colorPreview: true,
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'text_style',
                                    title: 'Text Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'copy_text',
                                    title: 'Copy text',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'copy_text_style',
                                    title: 'Copy Text Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'link',
                                    title: 'Link',
                                    default: ''
                                },
                                customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
                            ],
                            canShow: (data: IPageComponentSubmenuBlockData) => data?.type === 'lists'
                        },
                        /**
                         * Colour Block
                         */
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'colour_block',
                            title: 'Colour Block',
                            itemName: 'Colour Block',
                            defaultSize: 1,
                            maxSize: 2,
                            complex: true,
                            fields: [
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'background_color',
                                    title: 'Background Color',
                                    options: BgColorOptions,
                                    colorPreview: true,
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'text_color',
                                    title: 'Text Color',
                                    options: ColorOptions,
                                    colorPreview: true,
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'title_copy',
                                    title: 'Title Copy',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'title_copy_style',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Title Copy Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'title',
                                    title: 'Title',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'title_style',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'Title Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'cta_text',
                                    title: 'CTA Text',
                                    default: ''
                                },
                                <NICmsContentField.IChoiceField>{
                                    key: 'cta_style',
                                    type: NICmsContentField.EFieldType.choice,
                                    title: 'CTA Style',
                                    options: TextStyleOptions,
                                    default: ''
                                },
                                <NICmsContentField.IField>{
                                    type: NICmsContentField.EFieldType.text,
                                    key: 'link',
                                    title: 'CTA Link',
                                    default: ''
                                },
                                customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
                            ],
                            canShow: (data: IPageComponentSubmenuBlockData) => data?.type === 'colour_block'
                        },
                        <NICmsContentField.IField>{
                            key: 'hide',
                            type: NICmsContentField.EFieldType.toggle,
                            title: 'Hide',
                            description: 'Temporarily hide the block',
                            default: false
                        },
                        customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
                    ]
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'background_color',
                    title: 'Background Color',
                    options: BgColorOptions,
                    colorPreview: true,
                    default: ''
                },
                <NICmsContentField.IField>{
                    key: 'hide',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Hide',
                    description: 'Temporarily hide the column',
                    default: false
                },
                customerGroupVisibilityCmsField(CUSTOMER_GROUPS)
            ]
        },
        /**
         * Promo bar
         */
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-promo-bar',
            title: 'Promo Bar'
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'promobar_text',
            title: 'Text',
            default: ''
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'promobar_link',
            title: 'Link',
            default: ''
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobar_text_style',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text Style',
            options: TextStyleOptions,
            default: ''
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'promobar_text_color',
            title: 'Text Color',
            options: ColorOptions,
            colorPreview: true,
            default: ''
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'promobar_background_color',
            title: 'Background Color',
            options: BgColorOptions,
            colorPreview: true,
            default: ''
        },
        <NICmsContentField.IField>{
            key: 'promobar_hide',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide',
            description: 'Temporarily hide the promo bar',
            default: false
        }
    ]
};
